import React, { useState } from "react";
import defaultImage from "../assets/default1.png"; // Default image path
import { getResearchPageSpec } from "../utils/professorResearchLink";
import { getCorpusMostRecent } from "../utils/parseProfessorCorpus";

const SavedCard = ({
  professor,
  onSaved,
  isSaved: initialSaved,
  type = "saved", // "saved" = original big centered card; "seen" = compact thumbnail row on small screens
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageSrc, setImageSrc] = useState(professor.photo || defaultImage); // Manage image source state
  const [isSaved, setIsSaved] = useState(initialSaved); // Initialize from parent

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleSave = async () => {
    try {
      // Notify parent to toggle save state
      await onSaved(professor.id, !isSaved);
      setIsSaved(!isSaved); // Update internal state
    } catch (error) {
      console.error("Error toggling save:", error);
    }
  };

  const mostRecentPapers = getCorpusMostRecent(professor.corpus);
  const researchSpec = getResearchPageSpec(professor);

  return (
    <div
      className={`saved-card ${
        isExpanded ? "col-span-full" : "col-span-1"
      } bg-white p-4 rounded-lg shadow-md flex flex-col ${
        isExpanded
          ? "items-center"
          : type === "seen"
            ? "items-stretch nav:items-center"
            : "items-center"
      } transition-all duration-300`}
    >
      {!isExpanded ? (
        <>
          {type === "seen" ? (
            <>
              {/* Recently seen: compact thumbnail left on small screens; big vertical card >=550px */}
              <div className="nav:hidden grid w-full grid-cols-[auto_1fr] gap-3 items-center text-left">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                  <img
                    src={imageSrc}
                    alt={professor.name}
                    className="w-full h-full object-cover"
                    onError={() => setImageSrc(defaultImage)}
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-800 leading-snug">
                    {professor.name}
                  </h3>
                  {professor.email ? (
                    <p className="text-gray-700 text-sm mt-1 break-all">
                      <a
                        href={`mailto:${professor.email}`}
                        className="text-blue-600 hover:underline"
                      >
                        {professor.email}
                      </a>
                    </p>
                  ) : (
                    <p className="text-gray-500 text-sm mt-1">Email not available</p>
                  )}
                  {professor.website ? (
                    <p className="text-gray-700 text-sm mt-1 break-all">
                      <a
                        href={professor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Visit My Website
                      </a>
                    </p>
                  ) : (
                    <p className="text-gray-500 text-sm mt-1">No Website Available</p>
                  )}
                  {researchSpec.type === "none" ? (
                    <p className="text-gray-500 text-sm mt-1">No research page available</p>
                  ) : (
                    <p className="text-gray-700 text-sm mt-1">
                      <a
                        href={researchSpec.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {researchSpec.label}
                      </a>
                    </p>
                  )}
                  <p className="text-gray-700 text-sm mt-1 leading-snug">{professor.school}</p>
                  <p className="text-gray-700 text-sm leading-snug">{professor.department}</p>
                </div>
              </div>

              <div className="hidden nav:flex flex-col items-center w-full">
                <div className="image-container w-full h-60 mb-4 mt-4 overflow-hidden rounded-lg flex items-center justify-center">
                  <img
                    src={imageSrc}
                    alt={professor.name}
                    className="rounded-lg h-full object-cover"
                    onError={() => setImageSrc(defaultImage)}
                  />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800">{professor.name}</h3>
                {professor.email ? (
                  <p className="text-gray-700 text-lg mb-2">
                    <a
                      href={`mailto:${professor.email}`}
                      className="text-blue-500 hover:underline break-all"
                    >
                      {professor.email}
                    </a>
                  </p>
                ) : (
                  <p className="text-gray-500 text-lg mb-2">Email not available</p>
                )}
                {professor.website ? (
                  <p className="text-gray-700 text-md mb-2">
                    <a
                      href={professor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline break-all"
                    >
                      Visit My Website
                    </a>
                  </p>
                ) : (
                  <p className="text-gray-500 text-md mb-2">No Website Available</p>
                )}
                {researchSpec.type === "none" ? (
                  <p className="text-gray-500 text-md mb-3">No research page available</p>
                ) : (
                  <p className="text-gray-700 text-md mb-3">
                    <a
                      href={researchSpec.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      {researchSpec.label}
                    </a>
                  </p>
                )}
                <p className="text-gray-700 text-md">{professor.school}</p>
                <p className="text-gray-700 text-md">{professor.department}</p>
              </div>
            </>
          ) : (
            /* Saved professors: original — large photo centered above text at all breakpoints */
            <div className="flex flex-col items-center w-full">
              <div className="image-container w-full h-60 mb-4 mt-4 overflow-hidden rounded-lg flex items-center justify-center">
                <img
                  src={imageSrc}
                  alt={professor.name}
                  className="rounded-lg h-full object-cover"
                  onError={() => setImageSrc(defaultImage)}
                />
              </div>
              <h3 className="text-2xl font-semibold text-gray-800">{professor.name}</h3>
              {professor.email ? (
                <p className="text-gray-700 text-lg mb-2">
                  <a
                    href={`mailto:${professor.email}`}
                    className="text-blue-500 hover:underline break-all"
                  >
                    {professor.email}
                  </a>
                </p>
              ) : (
                <p className="text-gray-500 text-lg mb-2">Email not available</p>
              )}
              {professor.website ? (
                <p className="text-gray-700 text-md mb-2">
                  <a
                    href={professor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline break-all"
                  >
                    Visit My Website
                  </a>
                </p>
              ) : (
                <p className="text-gray-500 text-md mb-2">No Website Available</p>
              )}
              {researchSpec.type === "none" ? (
                <p className="text-gray-500 text-md mb-3">No research page available</p>
              ) : (
                <p className="text-gray-700 text-md mb-3">
                  <a
                    href={researchSpec.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    {researchSpec.label}
                  </a>
                </p>
              )}
              <p className="text-gray-700 text-md">{professor.school}</p>
              <p className="text-gray-700 text-md">{professor.department}</p>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col nav:flex-row w-full">
          {/* Small screens: photo above expanded text (matches Recommendations) */}
          <div className="nav:hidden w-full">
            <div className="image-container my-4 w-full">
              <img
                src={imageSrc}
                alt={professor.name}
                className="rounded-lg w-2/3 sm:w-1/2 h-auto object-cover mx-auto"
                onError={() => setImageSrc(defaultImage)}
              />
            </div>

            <div className="text-left px-2 sm:px-4">
              <p className="text-gray-700 text-base sm:text-lg mt-3">{professor.short_description}</p>
              <p className="text-gray-700 text-base sm:text-lg mt-3">{professor.additional_details}</p>

              <div className="mt-4">
                <h4 className="text-lg sm:text-xl text-gray-800 mt-3">Most Recent Papers:</h4>
                <ul className="list-disc pl-6 mt-2">
                  {mostRecentPapers.map((paper, index) => (
                    <li key={index} className="text-gray-700 text-base sm:text-lg">
                      {paper.title}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 pt-5 border-t border-gray-200">
                <div className="bg-gray-50 rounded-md p-4 text-gray-700 w-full min-w-0 text-base sm:text-lg">
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 leading-snug">
                    {professor.name}
                  </h3>
                  <p className="mt-1 text-base sm:text-lg font-medium text-gray-800">
                    {professor.school}
                  </p>
                  <p className="text-base sm:text-lg text-gray-700">
                    {professor.department}
                  </p>
                  {professor.email ? (
                    <p className="mt-2 text-sm sm:text-base break-all">
                      <a
                        href={`mailto:${professor.email}`}
                        className="text-gray-600 no-underline hover:underline"
                      >
                        {professor.email}
                      </a>
                    </p>
                  ) : (
                    <p className="mt-2 text-gray-500">Email not available</p>
                  )}
                  {professor.website ? (
                    <p className="mt-2 text-base sm:text-lg">
                      <a
                        href={professor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Visit My Website
                      </a>
                    </p>
                  ) : (
                    <p className="mt-2 text-base sm:text-lg">No Website Available</p>
                  )}

                  {researchSpec.type === "none" ? (
                    <p className="mt-1 text-gray-500 text-base sm:text-lg">No research page available</p>
                  ) : (
                    <p className="mt-1">
                      <a
                        href={researchSpec.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-base sm:text-lg"
                      >
                        {researchSpec.label}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 w-full flex justify-center">
                <button
                  type="button"
                  onClick={toggleSave}
                  className="bg-[#0196BF] text-white font-semibold text-base sm:text-lg px-6 py-2 rounded-full hover:bg-[#017BA3] transition z-20 w-3/4"
                >
                  {isSaved ? "Unsave" : "Save"}
                </button>
              </div>
            </div>
          </div>

          <div className="hidden nav:block text-content flex-1 lg:w-3/5 text-left px-2 sm:px-4 nav:px-8 lg:pr-20 mt-4 nav:mt-4">
            <h3 className="text-xl sm:text-2xl nav:text-3xl font-semibold text-gray-800">{professor.name}</h3>
            <p className="text-gray-700 text-base sm:text-lg mt-3 nav:mt-4">{professor.short_description}</p>
            <p className="text-gray-700 text-base sm:text-lg mt-3 nav:mt-4">{professor.additional_details}</p>

            <div className="mt-4 nav:mt-6">
              <h4 className="text-lg sm:text-xl text-gray-800 mt-3 nav:mt-4">Most Recent Papers:</h4>
              <ul className="list-disc pl-6 mt-2">
                {mostRecentPapers.map((paper, index) => (
                  <li key={index} className="text-gray-700 text-base sm:text-lg">
                    {paper.title}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="hidden nav:flex w-full nav:w-2/5 lg:w-1/4 flex-col items-start text-left mt-2 nav:mt-0 min-w-0">
            <div className="hidden nav:block image-container my-4 nav:my-6 w-full">
              <img
                src={imageSrc}
                alt={professor.name}
                className="rounded-lg w-full h-auto object-cover"
                onError={() => setImageSrc(defaultImage)}
              />
            </div>

            <div className="text-gray-700 w-full min-w-0 text-base sm:text-lg bg-gray-50 rounded-md p-4">
              <p className="text-base sm:text-lg">{professor.school}</p>
              <p className="text-base sm:text-lg">{professor.department}</p>
              {professor.email ? (
                <p className="mt-2 text-sm sm:text-base break-all">
                  <a
                    href={`mailto:${professor.email}`}
                    className="text-gray-600 no-underline hover:underline"
                  >
                    {professor.email}
                  </a>
                </p>
              ) : (
                <p className="mt-2 text-gray-500">Email not available</p>
              )}
              {professor.website ? (
                <p className="mt-2 text-base sm:text-lg">
                  <a
                    href={professor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Visit My Website
                  </a>
                </p>
              ) : (
                <p className="mt-2 text-base sm:text-lg">No Website Available</p>
              )}

              {researchSpec.type === "none" ? (
                <p className="mt-1 text-gray-500 text-base sm:text-lg">No research page available</p>
              ) : (
                <p className="mt-1">
                  <a
                    href={researchSpec.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-base sm:text-lg"
                  >
                    {researchSpec.label}
                  </a>
                </p>
              )}
            </div>

            <div className="mt-4 w-full flex justify-center">
              <button
                type="button"
                onClick={toggleSave}
                className="bg-[#0196BF] text-white font-semibold text-base sm:text-lg px-6 py-2 rounded-full hover:bg-[#017BA3] transition z-20 w-3/4 nav:w-full"
              >
                {isSaved ? "Unsave" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}



      <div className="flex justify-center w-full mt-4">
        <button
          onClick={toggleExpand}
          className="expand-button flex items-center justify-center"
        >
          {isExpanded ? (
            <span className="material-icons text-gray-600">expand_less</span>
          ) : (
            <span className="material-icons text-gray-600">expand_more</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default SavedCard;